// @flow
import React, { useContext } from 'react';
import { useFormikContext } from 'formik';

import { CNBCLegendContainer } from '../common/sgcnbc/CNBCLegendContainer';
import { Spacer } from '../presentational/spacing/Spacer';
import { If } from '../presentational/If';
import { PaddedAndRaisedSegment } from '../presentational/PaddedAndRaisedSegment';
import { FieldArrayGrid, FieldArrayGridConfigItemContext } from '../../formik-utils/FieldArrayGrid';
import {
  CREDITS_AND_BURSTS_CONFIG_ARRAY_BACKEND_NAME,
  CREDITS_AND_BURSTS_JSON_BACKEND_NAME,
  ESTIMATE_CNBC_DETAILS_FORM,
} from '../../constants';
import { CREDITS_AND_BURSTS_CONFIG_ARRAY_CONFIG } from '../../configuration/credits-and-bursts';
import { EstimateContext } from '../../contexts/EstimateContext';
import { isAnythingEditedOnEstimatePageBesidesTheGivenForm } from '../views/utils';
import { useIsUserAdmin } from '../../custom-hooks/useIsUserAdmin';
import { useIsSGCNBCApplicable } from '../../custom-hooks/useIsSGCNBCApplicable';

import { CreditAndBurstsConfigurationItem } from './CreditAndBurstsConfigurationItem';
import { CNBCAutomationToggleContainer } from './CNBCAutomationToggleContainer';
import { CNBCSGEditabilityMessageContainer } from './CNBCSGEditabilityMessageContainer';
import { getCNBCGridPaddingLeftBasedOnTheNumberOfRows } from './utils';

export const CreditsAndBurstsContainer = ({ readOnlyMode }) => {
  const estimate = useContext(EstimateContext);
  const { values, initialValues } = useFormikContext();
  const readOnlyModeFromProps = readOnlyMode;
  const isUserAdmin = useIsUserAdmin();
  const isSGCNBCApplicable = useIsSGCNBCApplicable();

  const disabled = isAnythingEditedOnEstimatePageBesidesTheGivenForm(
    values,
    initialValues,
    ESTIMATE_CNBC_DETAILS_FORM
  );

  const pathToFollowInValues = [
    CREDITS_AND_BURSTS_JSON_BACKEND_NAME,
    CREDITS_AND_BURSTS_CONFIG_ARRAY_BACKEND_NAME,
  ];

  // Get current CNBC rows from form values
  const cnbcRows =
    values?.[CREDITS_AND_BURSTS_JSON_BACKEND_NAME]?.[
      CREDITS_AND_BURSTS_CONFIG_ARRAY_BACKEND_NAME
    ] || [];

  const calendarYears = Array.from(
    { length: estimate?.deal_duration?.deal_duration_years ?? 0 },
    (_, i) => ({
      year_number: i + 1,
      calendar_year: `Year ${i + 1}`,
    })
  );
  const areAllTheYearsTaken = calendarYears?.every((year) =>
    cnbcRows.some((cnbcInputObj) => cnbcInputObj.year_number === year.year_number)
  );

  const shouldCNBCToggleBeDisabled = readOnlyMode || disabled;
  return (
    <>
      <PaddedAndRaisedSegment disabled={disabled}>
        <If test={isSGCNBCApplicable}>
          <CNBCLegendContainer />
          <If test={!isUserAdmin}>
            <CNBCSGEditabilityMessageContainer />
          </If>
        </If>

        <If test={isSGCNBCApplicable}>
          <span>
            <Spacer y={20} />
            <CNBCAutomationToggleContainer readOnlyMode={shouldCNBCToggleBeDisabled} />
            <Spacer y={40} />
          </span>
        </If>
        <FieldArrayGrid
          addRowButtonDisabled={areAllTheYearsTaken}
          columnsConfiguration={CREDITS_AND_BURSTS_CONFIG_ARRAY_CONFIG}
          disabled={disabled || readOnlyMode}
          gridName="Credits And Bursts"
          {...(isSGCNBCApplicable && {
            paddingLeft: getCNBCGridPaddingLeftBasedOnTheNumberOfRows(values),
          })}
          pathToFollowInValues={pathToFollowInValues}
        >
          {(arrayHelpers, row, isCurrentlyBeingEdited, index, allRows, readOnlyMode) => (
            <FieldArrayGridConfigItemContext.Provider
              value={{
                allRows,
                arrayHelpers,
                columnsConfiguration: CREDITS_AND_BURSTS_CONFIG_ARRAY_CONFIG,
                estimate,
                index,
                isCurrentlyBeingEdited,
                pathToFollowInValues: pathToFollowInValues,
                rowValues: row,
                readOnlyMode,
              }}
            >
              <CreditAndBurstsConfigurationItem
                calendarYears={calendarYears}
                cnbcInputs={cnbcRows}
                readOnlyMode={readOnlyModeFromProps}
              />
            </FieldArrayGridConfigItemContext.Provider>
          )}
        </FieldArrayGrid>
      </PaddedAndRaisedSegment>
    </>
  );
};
